import { GUIDE_NAME, markSegments, type GuidePose, type GuideStep } from "./guide";
import { mountBram, preloadStills, stepStill } from "./bram/bramView";
import { Typewriter } from "./typewriter";
import { moodFor } from "./bram/bramBrain";

/**
 * The guide's DOM pieces (DESIGN §3, mockups 1–5b): the speech bubble with Captain Bram's bust and name tag, the tip
 * with its drain and ✕, the welcome card, the glove. Builders only; guideOverlay.ts places them and wires behaviour.
 * Text is always built from markSegments() into text nodes and <b>, never innerHTML (scripts are plain strings).
 */

/** The placeholder glove (art ask A1: interface/guide/hand_point.png), mockups/gen.py's inline SVG. Trusted markup. */
const GLOVE_SVG = `<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M20 36V9a5 5 0 0 1 10 0v21h1a5 5 0 0 1 9 2v1a5 5 0 0 1 9 2v1a5 5 0 0 1 9 3v10c0 9-7 15-16 15h-7c-7 0-11-4-15-10l-8-12a5 5 0 0 1 8-6z" fill="#fffaf0" stroke="#3a2410" stroke-width="3" stroke-linejoin="round"/><path d="M31 31v8M40 34v6M49 37v5" stroke="#3a2410" stroke-width="2.5" stroke-linecap="round"/><path d="M22 58c3 2 6 3 10 3" stroke="#e2b455" stroke-width="3" stroke-linecap="round" fill="none"/></svg>`;

export const SKIP_LABEL = "Skip tips";
export const CONTINUE_LABEL = "Tap to continue ▸";
/** The confirm behind "Skip tips" (DESIGN §3.2): the walkthrough's, and a hint's (which turns battle tips off). */
export const SKIP_WALKTHROUGH_TEXT = "Skip my walkthrough? I'll be in the **⚙ menu → Guide** whenever you want it again.";
export const SKIP_HINTS_TEXT = "Silence my battle tips? Bring them back any time from the **⚙ menu → Guide**.";
export const KEEP_GOING = "Keep going";
export const SKIP_CONFIRM = "Skip";
const DEFAULT_CTA = "OK";

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Writes `**x**`-marked text as text nodes and <b>; a no-op when the text has not changed. */
export function setMarkedText(target: HTMLElement, text: string): void {
  if (target.dataset.text === text) return;
  target.dataset.text = text;
  target.replaceChildren(...markSegments(text).map((s) => (s.bold ? el("b", "", s.text) : document.createTextNode(s.text))));
}

/** Drawn heights (CSS px) of the captain: the bubble's bust, the tip's, the welcome card's figure (guide.css). */
export const BramPx = { Bust: 124, Tip: 90, Hero: 300 } as const;

/** The busts a first session shows, fetched once so a pose change never pops in. */
export function preloadPoses(): void { preloadStills(BramPx.Bust); }

export function buildGlove(): { hand: HTMLElement; ripple: HTMLElement } {
  const hand = el("div", "g-hand");
  hand.innerHTML = GLOVE_SVG;
  return { hand, ripple: el("div", "g-ripple") };
}

/** Walkthrough pips: `pip` is [position (1-based), count]. */
export function buildPips(pip: readonly [number, number] | undefined): HTMLElement {
  const box = el("span", "g-pips");
  if (!pip) return box;
  const [at, n] = pip;
  box.setAttribute("aria-label", `Step ${at} of ${n}`);
  for (let i = 1; i <= n; i++) box.append(el("i", i < at ? "done" : i === at ? "on" : ""));
  return box;
}

export interface BubbleParts {
  readonly wrap: HTMLElement;
  readonly bubble: HTMLElement;
  /** The captain's slot (bram/bramView.ts mounts him here). */
  readonly bust: HTMLElement;
  readonly text: HTMLElement;
  readonly cue: HTMLElement;
  readonly arrow: HTMLElement;
  readonly chip: HTMLButtonElement | null;
  readonly confirm: HTMLElement | null;
  readonly keep: HTMLButtonElement | null;
  readonly skip: HTMLButtonElement | null;
}

/**
 * The speech bubble (`do` and `info`): bust breaking the left edge, gold name tag, 16 px text, pips and the continue
 * cue in the footer, a notch toward the target. An info bubble is a <button> so keyboard and VoiceOver can continue.
 * `skip` adds the Skip chip and its confirm (`walkthrough` picks the confirm's words).
 */
export function buildBubble(step: GuideStep, skip: false | "walkthrough" | "hints"): BubbleParts {
  const wrap = el("div", "g-bwrap");
  const bubble = el(step.kind === "info" ? "button" : "div", `g-bubble ${step.kind}`);
  if (step.kind === "info") { (bubble as HTMLButtonElement).type = "button"; bubble.setAttribute("aria-describedby", "g-text"); }
  const bust = el("span", "g-bust");
  bust.append(stepStill(step, BramPx.Bust));
  const text = el("span", "g-text");
  text.id = "g-text";
  text.setAttribute("role", "status");
  text.setAttribute("aria-live", "polite");
  const cue = el("span", "g-cont", step.kind === "info" ? CONTINUE_LABEL : "");
  cue.setAttribute("aria-hidden", "true");
  const foot = el("span", "g-foot");
  foot.append(buildPips(step.pip), cue);
  // A `do` line with no pips has nothing for its footer: no empty strip under the words.
  foot.hidden = step.kind !== "info" && !step.pip;
  const arrow = el("span", "g-arrow");
  bubble.append(el("span", "g-name", GUIDE_NAME.toUpperCase()), bust, text, foot, arrow);
  wrap.append(bubble);
  if (!skip) return { wrap, bubble, bust, text, cue, arrow, chip: null, confirm: null, keep: null, skip: null };

  const chip = el("button", "g-skip", SKIP_LABEL);
  chip.type = "button";
  const confirm = el("div", "g-confirm");
  confirm.hidden = true;
  confirm.setAttribute("role", "alertdialog");
  const q = el("p", "");
  setMarkedText(q, skip === "walkthrough" ? SKIP_WALKTHROUGH_TEXT : SKIP_HINTS_TEXT);
  const row = el("div", "g-row");
  const keep = el("button", "btn gold g-keep", KEEP_GOING);
  const yes = el("button", "btn dark g-yes", SKIP_CONFIRM);
  keep.type = "button"; yes.type = "button";
  row.append(keep, yes);
  confirm.append(q, row);
  wrap.append(chip, confirm);
  return { wrap, bubble, bust, text, cue, arrow, chip, confirm, keep, skip: yes };
}

export interface TipParts {
  readonly tip: HTMLElement;
  /** The captain's slot (live like the bubble's). */
  readonly bust: HTMLElement;
  readonly text: HTMLElement; readonly close: HTMLButtonElement; readonly drain: HTMLElement; readonly arrow: HTMLElement;
}

/** A compact tip (mockup 5): 72 px bust, 15 px text, a 44 px ✕, and a gold drain bar showing the time left. */
export function buildTip(step: GuideStep): TipParts {
  const tip = el("div", "g-tip");
  const bust = el("span", "g-bust");
  bust.append(stepStill(step, BramPx.Tip));
  const text = el("span", "g-text");
  text.setAttribute("role", "status");
  text.setAttribute("aria-live", "polite");
  const close = el("button", "g-x", "✕");
  close.type = "button";
  close.setAttribute("aria-label", "Close tip");
  const drainBar = el("span", "g-drain");
  const drain = el("i", "");
  drainBar.append(drain);
  const arrow = el("span", "g-arrow");
  tip.append(bust, text, close, drainBar, arrow);
  return { tip, bust, text, close, drain, arrow };
}

export interface WelcomeParts { readonly root: HTMLElement; readonly hero: HTMLElement; readonly text: HTMLElement; readonly cta: HTMLButtonElement; readonly alt: HTMLButtonElement | null }

/** The welcome card (mockup 1): gold ribbon, the big cheering captain, a parchment card, one gold CTA and the alt link. */
export function buildWelcome(step: GuideStep): WelcomeParts {
  const root = el("div", "g-welcome");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-labelledby", "g-welcome-title");
  const stack = el("div", "g-wstack");
  const ribbon = el("div", "g-ribbon", step.title ?? "");
  ribbon.id = "g-welcome-title";
  const hero = el("div", "g-hero");
  hero.append(stepStill(step, BramPx.Hero));
  const card = el("div", "g-card");
  const text = el("p", "g-text");
  const cta = el("button", "btn gold g-cta", step.cta ?? DEFAULT_CTA);
  cta.type = "button";
  card.append(el("span", "g-plate", GUIDE_NAME.toUpperCase()), text, cta);
  let alt: HTMLButtonElement | null = null;
  if (step.altCta) { alt = el("button", "g-alt", step.altCta); alt.type = "button"; card.append(alt); }
  if (step.title) stack.append(ribbon);
  stack.append(hero, card);
  root.append(stack);
  return { root, hero, text, cta, alt };
}

/** A line the Guide sheet replays (DESIGN §3.6): the captain's words, no target. */
export interface GuideLine { readonly text: string; readonly pose: GuidePose }

/**
 * Plays lines one at a time as info cards inside `host` (the Guide sheet's topic replay): the captain (live when he
 * can be), the line typed out, pips, tap to continue (a tap while it types shows the rest first); `done` after the
 * last. Returns a function that stops early (the sheet closed).
 */
export function playLines(host: HTMLElement, lines: readonly GuideLine[], done: () => void): () => void {
  if (lines.length === 0) { done(); return () => {}; }
  const card = el("button", "g-bubble info g-replay");
  card.type = "button";
  card.setAttribute("aria-label", "Continue");
  const bust = el("span", "g-bust");
  const text = el("span", "g-text");
  text.setAttribute("role", "status");
  text.setAttribute("aria-live", "polite");
  const foot = el("span", "g-foot");
  card.append(el("span", "g-name", GUIDE_NAME.toUpperCase()), bust, text, foot);
  let i = 0;
  let over = false;
  const bram = mountBram(bust, { framing: "bust", mood: moodFor({ kind: "info", pose: lines[0].pose === "pointing" ? "neutral" : lines[0].pose, screen: "hub" }), kind: "info", cssPx: BramPx.Bust });
  const cont = el("span", "g-cont", CONTINUE_LABEL);
  const typer = new Typewriter(text, "info", (sp) => bram.say(sp), () => cont.classList.add("on"));
  const render = () => {
    const line = lines[i];
    cont.classList.remove("on");
    foot.replaceChildren(buildPips(lines.length > 1 ? [i + 1, lines.length] : undefined), cont);
    typer.set(line.text, true);
    if (!typer.running) cont.classList.add("on");
  };
  const stop = () => { if (over) return; over = true; typer.stop(); bram.release(); card.remove(); };
  card.addEventListener("click", (e) => {
    e.stopPropagation();
    if (over) return;
    if (typer.running) { typer.finish(); return; }
    if (++i < lines.length) { render(); return; }
    stop();
    done();
  });
  render();
  host.append(card);
  bram.poke();
  return stop;
}
